﻿/**
 * Onboarding Step 2 — pick a Form Fields preset.
 *
 * Shown right after the business basics + mode are picked, before the user
 * lands on the dashboard. Embeds the same `PresetPicker` used in Settings →
 * Forms; picking a level immediately applies the preset and advances. A "Skip"
 * link applies the General/Standard fallback so we never leave a business
 * with `business_type === null` after onboarding.
 *
 * Visual: a dark full-page overlay with a centered card holding the picker.
 * Matches the BusinessSetupPage's dark theme so the transition is seamless.
 */
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import type { ApplyPresetRequest } from "../../api/types";
import { useApplyPreset, usePresetCatalog } from "../../hooks/useFormPresets";
import { useToast } from "../../hooks/useToast";
import { PresetPicker } from "../formFields/PresetPicker";

interface Props {
  onDone: () => void;
}

export function OnboardingPresetStep({ onDone }: Props) {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { data: catalog, isLoading: catalogLoading } = usePresetCatalog();
  const applyPreset = useApplyPreset();

  function apply(req: ApplyPresetRequest, toastKey = "settings.formFields.saved") {
    applyPreset.mutate(req, {
      onSuccess: () => {
        addToast(t(toastKey), "success");
        onDone();
      },
      onError: (err) =>
        addToast(err.detail ?? t("settings.formFields.saveError"), "error"),
    });
  }

  function handleSkip() {
    // Skip still applies a preset (General/Essential) — say so explicitly,
    // otherwise the user has no idea their forms were just configured.
    apply(
      { business_type: "general", level: "essential" },
      "onboarding.presetSkipApplied",
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/95 backdrop-blur p-4 sm:p-8"
      data-testid="onboarding-preset-step"
    >
      <div className="w-full max-w-5xl rounded-3xl bg-white dark:bg-slate-900 p-6 sm:p-8 shadow-2xl ring-1 ring-slate-200 dark:ring-slate-800">
        {catalogLoading || !catalog ? (
          <div className="flex items-center justify-center py-24 text-sm text-slate-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            {t("common.loading")}…
          </div>
        ) : (
          <>
            <PresetPicker
              catalog={catalog}
              onPick={(business_type, level) => apply({ business_type, level })}
              isApplying={applyPreset.isPending}
            />
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={handleSkip}
                disabled={applyPreset.isPending}
                data-testid="onboarding-preset-skip"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-fg-secondary hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-50 transition-colors"
              >
                {applyPreset.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("settings.formPresets.onboarding.skip")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
